import React, { useState, useEffect, useMemo, useRef } from 'react';
const API = process.env.REACT_APP_BACKEND_URL || '';

// ---- Constants ----
const DEPT_ORDER = ['RM', 'FG', 'UltraNutri FG'];
const EPS = 1e-6;
const round2 = (n) => Math.round(n * 100) / 100;

// ---- Formatters ----
const fmtINR = (n, compact = false) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (compact) {
    if (abs >= 1e7) return sign + '₹' + (abs / 1e7).toFixed(2) + ' Cr';
    if (abs >= 1e5) return sign + '₹' + (abs / 1e5).toFixed(2) + ' L';
    if (abs >= 1e3) return sign + '₹' + (abs / 1e3).toFixed(1) + 'K';
    return sign + '₹' + abs.toFixed(0);
  }
  return sign + '₹' + abs.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const fmtNum = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const shortDate = (g) => { if (!g) return ''; const p = g.split(' '); return p[0] + ' ' + p[1]; };

// ---- Nice-tick math ----
const niceStep = (raw) => {
  if (raw <= 0) return 1;
  const exp = Math.floor(Math.log10(raw));
  const base = Math.pow(10, exp);
  const f = raw / base;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * base;
};
const makeTicks = (max, count) => {
  if (max <= 0) return [0, 1];
  const step = niceStep(max / count);
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= niceMax + step * 0.001; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
};
const roundedTopRectPath = (x, yTop, w, h, r) => {
  r = Math.min(r, w / 2, h);
  if (h <= 0) return '';
  return `M${x},${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + w - r} Q${x + w},${yTop} ${x + w},${yTop + r} V${yTop + h} H${x} Z`;
};

// ---- Per-period movement group (Consumption or Received) by Department ----
const buildMovement = (items, qKey, vKey, itemsCountKey) => {
  const departments = DEPT_ORDER.map((dept) => {
    const deptItems = items.filter((it) => it.d === dept && it[qKey] > EPS);
    const amount = deptItems.reduce((s, it) => s + it[vKey], 0);
    const sorted = [...deptItems].sort((a, b) => b[vKey] - a[vKey]);
    const row = { dept, amount: round2(amount) };
    row[itemsCountKey] = deptItems.length;
    row.items = sorted.map((it) => ({
      n: it.n, u: it.u, q: it[qKey],
      r: it[qKey] > EPS ? it[vKey] / it[qKey] : null, a: it[vKey],
    }));
    return row;
  });
  const grand_total = round2(departments.reduce((s, d) => s + d.amount, 0));
  return { departments, grand_total };
};

// ---- Period-range merge (ported verbatim from v2 HTML) ----
const computeData = (periods, fromId, toId) => {
  if (fromId > toId) [fromId, toId] = [toId, fromId];
  const periodIds = Object.keys(periods).sort();
  const range = periodIds.filter((id) => id >= fromId && id <= toId);
  const eff = range.length ? range : [toId];
  const startId = eff[0], endId = eff[eff.length - 1];
  const startItems = new Map(periods[startId].items.map((it) => [it.id, it]));
  const endItems = new Map(periods[endId].items.map((it) => [it.id, it]));
  const merged = new Map();
  const get = (id) => {
    let m = merged.get(id);
    if (!m) { m = { id, s: null, n: '', d: '', u: '', oq: 0, ov: 0, rq: 0, rv: 0, iq: 0, iv: 0, cq: 0, cv: 0, age: null }; merged.set(id, m); }
    return m;
  };
  eff.forEach((id) => {
    periods[id].items.forEach((it) => {
      const m = get(it.id);
      m.n = it.n; m.d = it.d; m.u = it.u || m.u; if (it.s != null) m.s = it.s;
      m.rq += it.rq; m.rv += it.rv; m.iq += it.iq; m.iv += it.iv;
    });
  });
  startItems.forEach((it, id) => { const m = get(id); m.oq = it.oq; m.ov = it.ov; m.n = m.n || it.n; m.d = m.d || it.d; m.u = m.u || it.u; });
  endItems.forEach((it, id) => { const m = get(id); m.cq = it.cq; m.cv = it.cv; m.age = it.age; m.n = it.n; m.d = it.d; m.u = it.u || m.u; m.s = it.s; });

  const items = [...merged.values()].map((m) => ({
    s: m.s, n: m.n, d: m.d, u: m.u, age: m.age,
    q: round2(m.cq), r: m.cq > EPS ? round2(m.cv / m.cq) : null, a: round2(m.cv),
    iq: round2(m.iq), iv: round2(m.iv), rq: round2(m.rq), rv: round2(m.rv),
  }));

  const departments = DEPT_ORDER.map((dept) => {
    const deptItems = items.filter((it) => it.d === dept);
    const amount = deptItems.reduce((s, it) => s + it.a, 0);
    return { dept, amount: round2(amount), items: deptItems.length };
  });
  const grand_total = round2(departments.reduce((s, d) => s + d.amount, 0));
  const consumption = buildMovement(items, 'iq', 'iv', 'items_consumed');
  const received = buildMovement(items, 'rq', 'rv', 'items_received');

  return {
    items, departments, grand_total,
    total_items: items.length,
    consumption, received,
    aging: periods[endId].aging || [],
    isRange: eff.length > 1,
    location: periods[endId].location,
    fromGenerated: periods[startId].generated,
    toGenerated: periods[endId].generated,
  };
};

// ---- Chart layout ----
const CHART_W = 520, CHART_H = 220, PAD_L = 54, PAD_R = 16, PAD_T = 16, PAD_B = 30;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

const ClosingChart = ({ totals, onHover }) => {
  const maxVal = Math.max(...totals.map((d) => d.closing), 0);
  const ticks = makeTicks(maxVal, 4);
  const niceMax = ticks[ticks.length - 1] || 1;
  const n = totals.length;
  const xAt = (i) => PAD_L + (n === 1 ? PLOT_W / 2 : (PLOT_W * i) / (n - 1));
  const yAt = (v) => PAD_T + PLOT_H - (v / niceMax) * PLOT_H;
  const linePts = totals.map((d, i) => `${xAt(i)},${yAt(d.closing)}`).join(' L');
  const [hoverIdx, setHoverIdx] = useState(-1);
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} style={{ width: '100%', height: 'auto' }}>
      {ticks.map((t) => (
        <g key={t}>
          <line className="chart-axis-line" x1={PAD_L} x2={CHART_W - PAD_R} y1={yAt(t)} y2={yAt(t)} />
          <text className="chart-axis-text" x={PAD_L - 8} y={yAt(t) + 3} textAnchor="end">{fmtINR(t, true)}</text>
        </g>
      ))}
      {totals.map((d, i) => <text key={`x-${i}`} className="chart-axis-text" x={xAt(i)} y={CHART_H - 8} textAnchor="middle">{shortDate(d.generated)}</text>)}
      <path d={`M${linePts}`} fill="none" stroke="var(--series-1)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {hoverIdx >= 0 && <line className="chart-crosshair" x1={xAt(hoverIdx)} x2={xAt(hoverIdx)} y1={PAD_T} y2={PAD_T + PLOT_H} style={{ opacity: 1 }} />}
      {totals.map((d, i) => (
        <g key={`m-${i}`}>
          <circle cx={xAt(i)} cy={yAt(d.closing)} r={hoverIdx === i ? 7 : 5} fill="var(--series-1)" stroke="var(--surface-1)" strokeWidth="2" />
          {i === n - 1 && <text className="chart-endpoint-label" x={xAt(i)} y={yAt(d.closing) - 12} textAnchor="end">{fmtINR(d.closing, true)}</text>}
          <circle className="chart-hit" cx={xAt(i)} cy={yAt(d.closing)} r="16"
            onPointerMove={(e) => { setHoverIdx(i); onHover({ x: e.clientX, y: e.clientY, html: `<strong>${d.generated}</strong><br/>Closing stock: <strong>${fmtINR(d.closing, false)}</strong>` }); }}
            onPointerLeave={() => { setHoverIdx(-1); onHover(null); }} />
        </g>
      ))}
    </svg>
  );
};

const FlowChart = ({ totals, onHover }) => {
  const maxVal = Math.max(...totals.map((d) => Math.max(d.receipt, d.issue)), 0);
  const ticks = makeTicks(maxVal, 4);
  const niceMax = ticks[ticks.length - 1] || 1;
  const n = totals.length;
  const groupW = PLOT_W / n;
  const barW = Math.min(24, groupW * 0.32);
  const gap = 2;
  const yAt = (v) => PAD_T + PLOT_H - (v / niceMax) * PLOT_H;
  return (
    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} style={{ width: '100%', height: 'auto' }}>
      {ticks.map((t) => (
        <g key={t}>
          <line className="chart-axis-line" x1={PAD_L} x2={CHART_W - PAD_R} y1={yAt(t)} y2={yAt(t)} />
          <text className="chart-axis-text" x={PAD_L - 8} y={yAt(t) + 3} textAnchor="end">{fmtINR(t, true)}</text>
        </g>
      ))}
      {totals.map((d, i) => {
        const gc = PAD_L + groupW * (i + 0.5);
        return (
          <g key={`b-${i}`}>
            <text className="chart-axis-text" x={gc} y={CHART_H - 8} textAnchor="middle">{shortDate(d.generated)}</text>
            {[
              { key: 'receipt', color: 'var(--series-1)', dx: -gap / 2 - barW, label: 'Receipt' },
              { key: 'issue', color: 'var(--series-2)', dx: gap / 2, label: 'Consumption' },
            ].map((spec) => {
              const val = d[spec.key];
              const barH = PLOT_H - (yAt(val) - PAD_T);
              const x = gc + spec.dx, yTop = yAt(val);
              return (
                <g key={spec.key}>
                  <path d={roundedTopRectPath(x, yTop, barW, barH, 4)} fill={spec.color} />
                  <rect className="chart-hit" x={x} y={PAD_T} width={barW} height={PLOT_H}
                    onPointerMove={(e) => onHover({ x: e.clientX, y: e.clientY, html: `<strong>${d.generated}</strong><br/>${spec.label}: <strong>${fmtINR(val, false)}</strong>` })}
                    onPointerLeave={() => onHover(null)} />
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

const SortArrow = ({ active, dir }) => active ? <span className="sort-arrow">{dir === 1 ? '▲' : '▼'}</span> : null;

// ---- Reusable Department bar table ----
const DeptBarTable = ({ rows, itemsKey, itemsLabel, valueLabel, barClass, expandable, itemColumns, emptyLabel, defaultSortKey = 'amount' }) => {
  const [sort, setSort] = useState({ key: defaultSortKey, dir: -1 });
  const [expanded, setExpanded] = useState(new Set());
  const maxV = Math.max(...rows.map((r) => r.amount), 0);
  const toggle = (key, isNum) => setSort(sort.key === key ? { key, dir: -sort.dir } : { key, dir: isNum ? -1 : 1 });
  const cmp = (a, b) => {
    const va = a[sort.key], vb = b[sort.key];
    if (typeof va === 'string') return (va || '').localeCompare(vb || '') * sort.dir;
    return ((va || 0) - (vb || 0)) * sort.dir;
  };
  const sorted = [...rows].sort(cmp);
  const cols = [
    { key: 'dept', label: 'Department', num: false },
    { key: itemsKey, label: itemsLabel, num: true },
    { key: 'amount', label: valueLabel, num: true, bar: true },
    { key: 'pct', label: '% of Total', num: true },
  ];
  return (
    <div className="dept-table-wrap">
      <table>
        <thead><tr>
          {cols.map((c) => (
            <th key={c.key} className={c.num ? 'num' : ''} style={{ cursor: 'pointer' }} onClick={() => toggle(c.key, c.num)}>
              {c.label}<SortArrow active={sort.key === c.key} dir={sort.dir} />
            </th>
          ))}
        </tr></thead>
        <tbody>
          {sorted.map((r) => {
            const w = maxV ? (r.amount / maxV * 100) : 0;
            const isOpen = expanded.has(r.dept);
            return (
              <React.Fragment key={r.dept}>
                <tr className={expandable ? `expandable-row${isOpen ? ' expanded' : ''}` : ''}
                  onClick={expandable ? () => { const nx = new Set(expanded); if (nx.has(r.dept)) nx.delete(r.dept); else nx.add(r.dept); setExpanded(nx); } : undefined}
                  style={expandable ? { cursor: 'pointer' } : undefined}>
                  <td className="name-cell">{expandable && <span className="chevron">{isOpen ? '▾' : '▸'}</span>}<span>{r.dept}</span></td>
                  <td className="num">{fmtNum(r[itemsKey])}</td>
                  <td className="row-bar-cell">
                    <div className="row-bar-wrap">
                      <div className="row-bar-track"><div className={`row-bar-fill ${barClass}`} style={{ width: w + '%' }} /></div>
                      <span className="row-bar-value">{fmtINR(r.amount, false)}</span>
                    </div>
                  </td>
                  <td className="num">{r.pct.toFixed(1)}%</td>
                </tr>
                {expandable && isOpen && (
                  <tr className="items-subrow"><td colSpan={4}>
                    {(r.itemsList || []).length === 0 ? <div className="items-empty">{emptyLabel}</div> : (
                      <div className="items-subtable-wrap"><table className="items-subtable">
                        <thead><tr>{itemColumns.map((c) => <th key={c.key} className={c.num ? 'num' : ''}>{c.label}</th>)}</tr></thead>
                        <tbody>{r.itemsList.map((it, i) => (
                          <tr key={i}>{itemColumns.map((c) => <td key={c.key} className={(c.num ? 'num' : '') + (c.key === 'n' ? ' name-cell' : '')}>{c.fmt(it)}</td>)}</tr>
                        ))}</tbody>
                      </table></div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---- Main ----
const RmfgPage = () => {
  const [periods, setPeriods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fromId, setFromId] = useState(null);
  const [toId, setToId] = useState(null);
  const [dailyRange, setDailyRange] = useState('7');
  const [dailySort, setDailySort] = useState({ key: 'id', dir: 1 });
  const [itemSort, setItemSort] = useState({ key: 's', dir: 1 });
  const [deptFilter, setDeptFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [negOnly, setNegOnly] = useState(false);
  const [activeFlowTab, setActiveFlowTab] = useState('none');
  const [agingExpanded, setAgingExpanded] = useState(new Set());
  const [tooltip, setTooltip] = useState(null);
  const itemDetailRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/rmfg/data`, { cache: 'no-store' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (cancelled) return;
        setPeriods(j);
        const ids = Object.keys(j).sort();
        setFromId(ids[ids.length - 1]); setToId(ids[ids.length - 1]);
      } catch (e) { if (!cancelled) setErr(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const periodIds = useMemo(() => (periods ? Object.keys(periods).sort() : []), [periods]);
  const DATA = useMemo(() => (periods && fromId && toId ? computeData(periods, fromId, toId) : null), [periods, fromId, toId]);

  const dailyTotals = useMemo(() => {
    if (!periods) return [];
    return periodIds.map((id) => {
      let closing = 0, receipt = 0, issue = 0;
      periods[id].items.forEach((it) => { closing += it.cv; receipt += it.rv; issue += it.iv; });
      return { id, generated: periods[id].generated, closing, receipt, issue };
    });
  }, [periods, periodIds]);
  const visibleDaily = useMemo(() => dailyRange === 'all' ? dailyTotals : dailyTotals.slice(-(parseInt(dailyRange, 10) || 7)), [dailyTotals, dailyRange]);
  const sortedDaily = useMemo(() => [...visibleDaily].sort((a, b) => {
    const va = a[dailySort.key], vb = b[dailySort.key];
    if (typeof va === 'string') return va.localeCompare(vb) * dailySort.dir;
    return (va - vb) * dailySort.dir;
  }), [visibleDaily, dailySort]);

  const deptRows = useMemo(() => {
    if (!DATA) return [];
    return DATA.departments.map((d) => ({ dept: d.dept, items: d.items, amount: d.amount, pct: DATA.grand_total ? (d.amount / DATA.grand_total * 100) : 0 }));
  }, [DATA]);
  const consRows = useMemo(() => {
    if (!DATA) return [];
    return DATA.consumption.departments.map((d) => ({ dept: d.dept, items_consumed: d.items_consumed, amount: d.amount, pct: DATA.consumption.grand_total ? (d.amount / DATA.consumption.grand_total * 100) : 0, itemsList: d.items || [] }));
  }, [DATA]);
  const recRows = useMemo(() => {
    if (!DATA) return [];
    return DATA.received.departments.map((d) => ({ dept: d.dept, items_received: d.items_received, amount: d.amount, pct: DATA.received.grand_total ? (d.amount / DATA.received.grand_total * 100) : 0, itemsList: d.items || [] }));
  }, [DATA]);

  const filteredItems = useMemo(() => {
    if (!DATA) return [];
    const q = searchQ.trim().toLowerCase();
    let rows = DATA.items.filter((it) => {
      if (deptFilter && it.d !== deptFilter) return false;
      if (q && !(it.n || '').toLowerCase().includes(q)) return false;
      if (negOnly && !(it.q < -1e-6 || it.a < -0.005)) return false;
      return true;
    });
    return rows.sort((a, b) => {
      const va = a[itemSort.key], vb = b[itemSort.key];
      if (typeof va === 'string') return (va || '').localeCompare(vb || '') * itemSort.dir;
      return ((va || 0) - (vb || 0)) * itemSort.dir;
    });
  }, [DATA, searchQ, negOnly, itemSort, deptFilter]);

  const negCount = useMemo(() => DATA ? DATA.items.filter((it) => it.q < -1e-6 || it.a < -0.005).length : 0, [DATA]);
  const onHover = (info) => setTooltip(info);
  const jumpToNeg = () => { setActiveFlowTab('none'); setDeptFilter(''); setSearchQ(''); setNegOnly(true); setTimeout(() => itemDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); };
  const toggleSort = (state, setState, key, isNum) => setState(state.key === key ? { key, dir: -state.dir } : { key, dir: isNum ? -1 : 1 });

  if (loading) return <div className="gs-root"><style>{DASHBOARD_CSS}</style><div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading Feed Plant data…</div></div>;
  if (err) return <div className="gs-root"><style>{DASHBOARD_CSS}</style><div style={{ padding: 40, color: 'var(--critical-text)' }}>Failed to load: {err}</div></div>;
  if (!DATA) return null;

  const consItemColumns = [
    { key: 'n', label: 'Product Name', num: false, fmt: (it) => it.n },
    { key: 'u', label: 'UOM', num: false, fmt: (it) => it.u || '—' },
    { key: 'q', label: 'Qty Consumed', num: true, fmt: (it) => fmtNum(it.q) },
    { key: 'r', label: 'Rate (₹, FIFO)', num: true, fmt: (it) => it.r != null ? fmtINR(it.r, false) : '—' },
    { key: 'a', label: 'Amount (₹, FIFO)', num: true, fmt: (it) => fmtINR(it.a, false) },
  ];
  const recItemColumns = [
    { key: 'n', label: 'Product Name', num: false, fmt: (it) => it.n },
    { key: 'u', label: 'UOM', num: false, fmt: (it) => it.u || '—' },
    { key: 'q', label: 'Qty Received', num: true, fmt: (it) => fmtNum(it.q) },
    { key: 'r', label: 'Rate (₹)', num: true, fmt: (it) => it.r != null ? fmtINR(it.r, false) : '—' },
    { key: 'a', label: 'Amount (₹)', num: true, fmt: (it) => fmtINR(it.a, false) },
  ];

  return (
    <div className="gs-root" data-testid="rmfg-page">
      <style>{DASHBOARD_CSS}</style>
      <div className="viz-root">
        <div className="wrap">
          <div className="header">
            <div>
              <h1>Feed Plant — RM & FG Inventory</h1>
              <div className="loc">{DATA.location} — as on {DATA.toGenerated}</div>
            </div>
          </div>

          <div className="filter-row period-filter-row">
            <label className="period-label">Period</label>
            <select value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="rmfg-period-from">
              {periodIds.map((id) => <option key={id} value={id}>{periods[id].generated}</option>)}
            </select>
            <span className="period-to-label">to</span>
            <select value={toId} onChange={(e) => { const v = e.target.value; setToId(v); if (fromId > v) setFromId(v); }} data-testid="rmfg-period-to">
              {periodIds.map((id) => <option key={id} value={id}>{periods[id].generated}</option>)}
            </select>
            <span className="period-note">
              {DATA.isRange
                ? `Opening as on ${DATA.fromGenerated} · Closing as on ${DATA.toGenerated} · Consumption & Received totalled ${DATA.fromGenerated} – ${DATA.toGenerated}`
                : `Showing ${DATA.toGenerated}`}
            </span>
          </div>

          <p className="card-sub" style={{ margin: '0 0 18px' }}>
            Consumption &amp; Closing values below use <strong>FIFO costing</strong>: each day's receipts are layered at their own per-unit cost (Rate × Qty), and consumption draws down the oldest layer first. Quantities and Received values match each day's register exactly; Consumption/Closing figures differ from the register's own moving-average valuation by design.
          </p>

          {/* KPIs */}
          <div className="kpi-row">
            <div className={`kpi hero kpi-clickable${activeFlowTab === 'none' ? ' active' : ''}`} role="button" tabIndex={0} onClick={() => setActiveFlowTab('none')}>
              <div className="label">Total closing stock value (FIFO)</div>
              <div className="value">{fmtINR(DATA.grand_total, true)}</div>
              <div className="sub">{fmtINR(DATA.grand_total, false)}</div>
            </div>
            <div className="kpi">
              <div className="label">Line items</div>
              <div className="value">{fmtNum(DATA.total_items)}</div>
              <div className="sub">as on {DATA.toGenerated}</div>
              {negCount > 0 && <button type="button" className="neg-flag" onClick={jumpToNeg}>⚠ {negCount} with negative closing stock</button>}
            </div>
            <div className={`kpi kpi-clickable${activeFlowTab === 'received' ? ' active' : ''}`} role="button" tabIndex={0} onClick={() => setActiveFlowTab('received')}>
              <div className="label">Received this period</div>
              <div className="value">{fmtINR(DATA.received.grand_total, true)}</div>
              <div className="sub">{fmtINR(DATA.received.grand_total, false)}</div>
            </div>
            <div className={`kpi kpi-clickable${activeFlowTab === 'consumption' ? ' active' : ''}`} role="button" tabIndex={0} onClick={() => setActiveFlowTab('consumption')}>
              <div className="label">Consumption this period</div>
              <div className="value">{fmtINR(DATA.consumption.grand_total, true)}</div>
              <div className="sub">{fmtINR(DATA.consumption.grand_total, false)}</div>
            </div>
          </div>

          {activeFlowTab === 'none' && (
            <>
              {/* Department Breakdown */}
              <div className="card overview-card">
                <p className="card-title">Department Breakdown</p>
                <p className="card-sub">Bars scale to each department's share of total value · click a column header to sort</p>
                <DeptBarTable rows={deptRows} itemsKey="items" itemsLabel="Line Items" valueLabel="Closing Stock Value (₹, FIFO)" barClass="receipt" />
              </div>

              {/* Daily Trend */}
              <div className="card overview-card">
                <p className="card-title">Daily Trend</p>
                <p className="card-sub">Closing stock (FIFO), receipts and consumption (FIFO) · independent of the period filter above</p>
                <div className="filter-row" style={{ marginBottom: 16 }}>
                  <select value={dailyRange} onChange={(e) => setDailyRange(e.target.value)} data-testid="rmfg-daily-range">
                    <option value="7">Last 7 days</option>
                    <option value="all">All time</option>
                  </select>
                </div>
                <div className="daily-trend-charts">
                  <div>
                    <div className="chart-label">Closing Stock Value</div>
                    <div className="chart-svg-wrap"><ClosingChart totals={visibleDaily} onHover={onHover} /></div>
                  </div>
                  <div>
                    <div className="chart-label">Receipts vs Consumption</div>
                    <div className="chart-legend">
                      <div className="chart-legend-item"><span className="chart-legend-swatch" style={{ background: 'var(--series-1)' }} /><span>Receipt</span></div>
                      <div className="chart-legend-item"><span className="chart-legend-swatch" style={{ background: 'var(--series-2)' }} /><span>Consumption</span></div>
                    </div>
                    <div className="chart-svg-wrap"><FlowChart totals={visibleDaily} onHover={onHover} /></div>
                  </div>
                </div>
                <div className="dept-table-wrap">
                  <table>
                    <thead><tr>
                      {[['id', 'Date', false], ['closing', 'Closing Stock Value (₹, FIFO)', true], ['receipt', 'Receipt Value (₹)', true], ['issue', 'Consumption Value (₹, FIFO)', true]].map(([k, lbl, num]) => (
                        <th key={k} className={num ? 'num' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleSort(dailySort, setDailySort, k, num)}>
                          {lbl}<SortArrow active={dailySort.key === k} dir={dailySort.dir} />
                        </th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {sortedDaily.map((d) => (
                        <tr key={d.id}>
                          <td>{d.generated}</td>
                          <td className="num">{fmtINR(d.closing, false)}</td>
                          <td className="num">{fmtINR(d.receipt, false)}</td>
                          <td className="num">{fmtINR(d.issue, false)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Stock Aging */}
              <div className="card overview-card">
                <p className="card-title">Stock Aging</p>
                <p className="card-sub">Closing stock as on {DATA.toGenerated}, aged by FIFO receipt layer · click a row to see the items in that age band</p>
                <div className="dept-table-wrap">
                  <table>
                    <thead><tr><th>Age</th><th className="num">Qty</th><th className="amount-th">Value (₹, FIFO)</th><th className="num">% of Total</th></tr></thead>
                    <tbody>
                      {(DATA.aging || []).map((b) => {
                        const maxV = Math.max(...(DATA.aging || []).map((x) => x.value), 0);
                        const widthPct = maxV ? (b.value / maxV * 100) : 0;
                        const isOpen = agingExpanded.has(b.label);
                        return (
                          <React.Fragment key={b.label}>
                            <tr className={`expandable-row${isOpen ? ' expanded' : ''}`}
                              onClick={() => { const nx = new Set(agingExpanded); if (nx.has(b.label)) nx.delete(b.label); else nx.add(b.label); setAgingExpanded(nx); }}
                              style={{ cursor: 'pointer' }}>
                              <td className="name-cell"><span className="chevron">{isOpen ? '▾' : '▸'}</span><span>{b.label}</span></td>
                              <td className="num">{fmtNum(b.qty)}</td>
                              <td className="row-bar-cell">
                                <div className="row-bar-wrap">
                                  <div className="row-bar-track"><div className="row-bar-fill receipt" style={{ width: widthPct + '%' }} /></div>
                                  <span className="row-bar-value">{fmtINR(b.value, false)}</span>
                                </div>
                              </td>
                              <td className="num">{b.pct.toFixed(1)}%</td>
                            </tr>
                            {isOpen && (
                              <tr className="items-subrow"><td colSpan={4}>
                                {(b.items || []).length === 0 ? <div className="items-empty">No stock currently in this age band.</div> : (
                                  <div className="items-subtable-wrap"><table className="items-subtable">
                                    <thead><tr><th>Department</th><th>Product Name</th><th>Received</th><th className="num">Age (days)</th><th className="num">Qty</th><th className="num">Value (₹)</th></tr></thead>
                                    <tbody>
                                      {b.items.map((it, i) => (
                                        <tr key={i}>
                                          <td>{it.d || '—'}</td>
                                          <td className="name-cell">{it.n}</td>
                                          <td>{it.date || '—'}</td>
                                          <td className="num">{fmtNum(it.age)}</td>
                                          <td className="num">{fmtNum(it.q)}</td>
                                          <td className="num">{fmtINR(it.v, false)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table></div>
                                )}
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Item-level Detail */}
              <div className="card overview-card" ref={itemDetailRef}>
                <p className="card-title">Item-level Detail</p>
                <p className="card-sub">Filter by department or search a product to drill into the numbers behind the chart · Rate/Amount are FIFO-costed · Age is the age in days of the oldest FIFO layer still on hand · check "Negative stock only" to isolate items showing negative Closing Qty/Value — a data-quality flag, not a valid physical stock level</p>
                <div className="filter-row">
                  <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} data-testid="rmfg-dept-filter">
                    <option value="">All departments</option>
                    {DATA.departments.map((d) => <option key={d.dept} value={d.dept}>{d.dept} ({d.items})</option>)}
                  </select>
                  <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search product name…" data-testid="rmfg-search" />
                  <label className="neg-toggle"><input type="checkbox" checked={negOnly} onChange={(e) => setNegOnly(e.target.checked)} data-testid="rmfg-neg-toggle" /> Negative stock only</label>
                  <span className="count">{filteredItems.length.toLocaleString('en-IN')} items</span>
                </div>
                <div className="item-table-scroll">
                  <table>
                    <thead><tr>
                      {[['s', 'S.No', true], ['n', 'Product Name', false], ['d', 'Department', false], ['u', 'UOM', false], ['q', 'Qty', true], ['r', 'Rate (₹, FIFO)', true], ['a', 'Amount (₹, FIFO)', true], ['age', 'Age (days)', true]].map(([k, lbl, num]) => (
                        <th key={k} className={num ? 'num' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleSort(itemSort, setItemSort, k, num)}>
                          {lbl}<SortArrow active={itemSort.key === k} dir={itemSort.dir} />
                        </th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {filteredItems.map((it, i) => (
                        <tr key={`${it.d}-${it.s}-${i}`}>
                          <td className="num">{fmtNum(it.s)}</td>
                          <td className="name-cell">{it.n}</td>
                          <td>{it.d || '—'}</td>
                          <td>{it.u || '—'}</td>
                          <td className={`num${it.q < -1e-6 ? ' negative-cell' : ''}`}>{fmtNum(it.q)}</td>
                          <td className="num">{it.r != null ? fmtINR(it.r, false) : '—'}</td>
                          <td className={`num${it.a < -0.005 ? ' negative-cell' : ''}`}>{fmtINR(it.a, false)}</td>
                          <td className="num">{it.age === null || it.age === undefined ? '—' : fmtNum(it.age)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeFlowTab === 'consumption' && (
            <div className="card">
              <p className="card-title">Department-wise Consumption</p>
              <p className="card-sub">Material consumed over the selected period, valued FIFO · click a row to see the items · click "Received this period" above to switch, or "Total closing stock value" to go back</p>
              <DeptBarTable rows={consRows} itemsKey="items_consumed" itemsLabel="Items Consumed" valueLabel="Consumption Value (₹, FIFO)" barClass="consumption" expandable itemColumns={consItemColumns} emptyLabel="No items consumed this period." />
            </div>
          )}
          {activeFlowTab === 'received' && (
            <div className="card">
              <p className="card-title">Department-wise Received</p>
              <p className="card-sub">Material received over the selected period, at the rate in each day's register · click a row to see the items · click "Consumption this period" above to switch, or "Total closing stock value" to go back</p>
              <DeptBarTable rows={recRows} itemsKey="items_received" itemsLabel="Items Received" valueLabel="Received Value (₹)" barClass="receipt" expandable itemColumns={recItemColumns} emptyLabel="No items received this period." />
            </div>
          )}

          <footer>Source: RM & FG Register, Feed Plant · Quantities and Received values reconciled exactly against each day's own register · Opening/Consumption/Closing values recomputed on a FIFO basis (receipts layered at their own per-unit cost, oldest layer consumed first) and will not match the register's own moving-average figures by design</footer>
        </div>
      </div>

      {tooltip && <div className="tooltip" style={{ opacity: 1, left: tooltip.x + 14, top: tooltip.y + 14 }} dangerouslySetInnerHTML={{ __html: tooltip.html }} />}
    </div>
  );
};

const DASHBOARD_CSS = `
.gs-root {
  --surface-1: #fcfcfb; --page-plane: #f9f9f7; --text-primary: #0b0b0b; --text-secondary: #52514e;
  --text-muted: #898781; --gridline: #e1e0d9; --baseline: #c3c2b7; --border: rgba(11,11,11,0.10);
  --series-1: #2a78d6; --series-2: #eb6834; --success-text: #006300; --critical-text: #d03b3b;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif; background: var(--page-plane); color: var(--text-primary); min-height: 100vh;
}
.gs-root * { box-sizing: border-box; }
.gs-root .viz-root { background: var(--page-plane); min-height: 100vh; padding: 28px 32px 60px; }
.gs-root .wrap { max-width: 1220px; margin: 0 auto; }
.gs-root .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 22px; flex-wrap: wrap; }
.gs-root .header h1 { font-size: 20px; font-weight: 650; margin: 0 0 4px; letter-spacing: -0.01em; }
.gs-root .header .loc { font-size: 12.5px; color: var(--text-muted); max-width: 640px; line-height: 1.5; }
.gs-root .kpi-row { display: grid; grid-template-columns: 1.4fr repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
.gs-root .kpi { background: var(--surface-1); border: 1px solid var(--border); border-radius: 10px; padding: 16px 18px; }
.gs-root .kpi .label { font-size: 12px; color: var(--text-secondary); margin-bottom: 8px; }
.gs-root .kpi .value { font-size: 26px; font-weight: 650; letter-spacing: -0.01em; }
.gs-root .kpi.hero .value { font-size: 32px; color: var(--series-1); }
.gs-root .kpi .sub { font-size: 11.5px; color: var(--text-muted); margin-top: 4px; }
.gs-root .kpi .neg-flag { display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; font-size: 11.5px; font-weight: 650; color: var(--critical-text); cursor: pointer; background: none; border: none; padding: 0; font-family: inherit; }
.gs-root .kpi .neg-flag:hover { text-decoration: underline; }
.gs-root .kpi-clickable { cursor: pointer; transition: box-shadow .12s, border-color .12s; }
.gs-root .kpi-clickable:hover { box-shadow: inset 0 0 0 1px var(--series-1); border-color: var(--series-1); }
.gs-root .kpi-clickable.active { box-shadow: inset 0 0 0 2px var(--series-1); border-color: var(--series-1); }
.gs-root .kpi-clickable.active .label, .gs-root .kpi-clickable.active .value { color: var(--series-1); }
.gs-root .card { background: var(--surface-1); border: 1px solid var(--border); border-radius: 10px; padding: 20px 22px; margin-bottom: 20px; }
.gs-root .card-title { font-size: 15px; font-weight: 650; margin: 0 0 4px; }
.gs-root .card-sub { font-size: 12px; color: var(--text-muted); margin: 0 0 14px; line-height: 1.55; }
.gs-root .filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.gs-root .period-filter-row { margin-bottom: 12px; }
.gs-root .period-label { font-size: 12px; font-weight: 650; color: var(--text-secondary); }
.gs-root .period-to-label, .gs-root .period-note { font-size: 12px; color: var(--text-muted); }
.gs-root select, .gs-root input[type=text] { font-size: 12.5px; padding: 6px 10px; border: 1px solid var(--border); border-radius: 6px; background: #fff; color: var(--text-primary); font-family: inherit; }
.gs-root input[type=text] { min-width: 240px; }
.gs-root .neg-toggle { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-secondary); cursor: pointer; }
.gs-root .count { font-size: 11.5px; color: var(--text-muted); margin-left: auto; }
.gs-root table { width: 100%; border-collapse: collapse; }
.gs-root thead th { text-align: left; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); font-weight: 650; padding: 8px 10px; border-bottom: 1px solid var(--border); }
.gs-root thead th.num, .gs-root thead th.amount-th { text-align: right; }
.gs-root thead th .sort-arrow { margin-left: 4px; font-size: 10px; color: var(--series-1); }
.gs-root tbody td { padding: 8px 10px; font-size: 12.5px; border-bottom: 1px solid var(--gridline); color: var(--text-primary); font-variant-numeric: tabular-nums; }
.gs-root tbody td.num { text-align: right; }
.gs-root tbody td.name-cell { max-width: 340px; white-space: normal; }
.gs-root tbody td.negative-cell { color: var(--critical-text); font-weight: 650; }
.gs-root tbody tr:hover { background: var(--page-plane); }
.gs-root .item-table-scroll, .gs-root .dept-table-wrap { overflow-x: auto; }
.gs-root .item-table-scroll table { min-width: 900px; }
.gs-root .item-table-scroll { max-height: 520px; overflow-y: auto; }
.gs-root .daily-trend-charts { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
@media (max-width: 900px) { .gs-root .daily-trend-charts { grid-template-columns: 1fr; } .gs-root .kpi-row { grid-template-columns: 1fr 1fr; } }
.gs-root .chart-label { font-size: 12px; font-weight: 650; color: var(--text-secondary); margin-bottom: 6px; }
.gs-root .chart-legend { display: flex; gap: 12px; margin-bottom: 6px; font-size: 11.5px; color: var(--text-secondary); }
.gs-root .chart-legend-item { display: inline-flex; align-items: center; gap: 5px; }
.gs-root .chart-legend-swatch { width: 10px; height: 10px; border-radius: 2px; }
.gs-root .chart-svg-wrap { background: #fff; border: 1px solid var(--border); border-radius: 8px; padding: 8px; }
.gs-root .chart-axis-line { stroke: var(--gridline); stroke-width: 1; }
.gs-root .chart-axis-text { font-size: 10px; fill: var(--text-muted); }
.gs-root .chart-endpoint-label { font-size: 11px; fill: var(--series-1); font-weight: 650; }
.gs-root .chart-crosshair { stroke: var(--series-1); stroke-width: 1; stroke-dasharray: 3 2; opacity: 0; pointer-events: none; }
.gs-root .chart-hit { fill: transparent; cursor: crosshair; }
.gs-root .row-bar-cell { padding: 6px 10px; min-width: 240px; }
.gs-root .row-bar-wrap { display: flex; align-items: center; gap: 10px; }
.gs-root .row-bar-track { flex: 1; height: 8px; background: var(--gridline); border-radius: 4px; overflow: hidden; }
.gs-root .row-bar-fill { height: 100%; border-radius: 4px; }
.gs-root .row-bar-fill.consumption { background: var(--series-2); }
.gs-root .row-bar-fill.receipt { background: var(--series-1); }
.gs-root .row-bar-value { font-size: 12px; font-variant-numeric: tabular-nums; font-weight: 650; min-width: 92px; text-align: right; }
.gs-root .expandable-row:hover, .gs-root .expandable-row.expanded { background: var(--page-plane); }
.gs-root .chevron { display: inline-block; width: 12px; color: var(--text-muted); margin-right: 4px; }
.gs-root .expandable-row.expanded .chevron { color: var(--series-1); }
.gs-root .items-subrow td { padding: 0 10px 14px; background: #fbfaf5; border-bottom: 1px solid var(--gridline); }
.gs-root .items-subtable-wrap { padding: 6px 6px 2px; max-height: 340px; overflow-y: auto; }
.gs-root .items-subtable { width: 100%; }
.gs-root .items-subtable thead th { font-size: 10.5px; padding: 4px 8px; }
.gs-root .items-subtable tbody td { font-size: 11.5px; padding: 4px 8px; }
.gs-root .items-empty { padding: 12px; font-size: 12px; color: var(--text-muted); font-style: italic; }
.gs-root .tooltip { position: fixed; pointer-events: none; background: #ffffff; border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px; font-size: 11.5px; box-shadow: 0 4px 14px rgba(0,0,0,0.10); z-index: 999; }
.gs-root footer { text-align: center; font-size: 11px; color: var(--text-muted); margin-top: 24px; }
`;

export default RmfgPage;
